import React, { useCallback, useEffect, useRef, useState } from 'react';
import codeMirrorLib from 'codemirror';
import { FormattedMessage, useIntl } from 'react-intl';
import 'codemirror/mode/htmlmixed/htmlmixed';
import 'codemirror/lib/codemirror.css';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';

import { getSupportedGroups } from './buttons/field-replacement';
import { messages as fieldReplacementMessages, mainMessages } from './buttons/field-replacement/messages';
import EnglishOnly from './component.english';
import { translateTokens } from './helpers';
import style from './style.module.less';
import IntlProvider from '../../intl/intl-provider';
import AuthenticatedLocaleContextProvider from '../../intl/locale-context-provider/authenticated-locale-context-provider';

const config = {
  lineNumbers: true,
};

/**
 * @param {object} props
 * @param {(content: string) => void} props.onChange
 * @param {string} props.value
 * @param {keyof typeof import('./constants').FIELD_REPLACEMENT_SETS} props.replacementSet
 * @returns {React.JSX.Element}
 */
export const CodeMirrorEditor = ({ onChange, value, replacementSet }) => {
  const intl = useIntl();
  const [isCodeMirrorInitialized, setIsCodeMirrorInitialized] = useState(false);
  const [fieldToInsert, setFieldToInsert] = useState(null);
  const [model, setModel] = useState(translateTokens(value, intl));

  const ref = useRef();
  const codeMirror = useRef();

  const onInstanceChange = useCallback((doc) => {
    setModel(doc.getValue());
  }, []);

  const onFieldClick = (msgKey) => {
    const translatedString = intl.formatMessage(fieldReplacementMessages[msgKey]);
    setFieldToInsert(translatedString);
  };
  useEffect(() => {
    if (!isCodeMirrorInitialized) {
      setIsCodeMirrorInitialized(true);
      codeMirror.current = codeMirrorLib.fromTextArea(ref.current, config);
      codeMirror.current.on('change', onInstanceChange);
      codeMirror.current.focus();
    }
    if (fieldToInsert) {
      const cursor = codeMirror.current.getCursor();
      const htmlArray = codeMirror.current.getValue().split('\n');
      const line = htmlArray[cursor.line];
      const newLine = `${line.substring(0, cursor.ch)}[${fieldToInsert}]${line.substring(cursor.ch)}`;
      htmlArray[cursor.line] = newLine;
      codeMirror.current.setValue(htmlArray.join('\n'));
      codeMirror.current.focus();
      codeMirror.current.setCursor(cursor.line, cursor.ch + fieldToInsert.length + 2);
      setFieldToInsert(null);
    }
  }, [fieldToInsert, isCodeMirrorInitialized, onInstanceChange]);

  const fieldReplacements = getSupportedGroups(replacementSet);

  return (
    <div className={style['packing-slip-complete-editor']} data-ignore-root-event-listeners>
      <div className={style['field-replacement-standalone-wrapper']}>
        <DropdownMenu>
          <DropdownMenuToggler className={style['field-replacement-button']} theme='white'>
            <FormattedMessage {...mainMessages.buttonText} />
          </DropdownMenuToggler>
          {Object.keys(fieldReplacements).map((groupKey) => {
            return (
              <React.Fragment key={groupKey}>
                <div className={style['group-header']}>{groupKey}</div>
                {fieldReplacements[groupKey].map((optionKey) => {
                  return (
                    <DropdownMenuItem key={optionKey} onClick={() => onFieldClick(optionKey)}>
                      {intl.formatMessage(fieldReplacementMessages[optionKey])}
                    </DropdownMenuItem>
                  );
                })}
              </React.Fragment>
            );
          })}
        </DropdownMenu>
      </div>
      <div className={style.divider} />
      <textarea ref={ref} defaultValue={model} />
      <AuthenticatedLocaleContextProvider>
        <IntlProvider>
          <EnglishOnly intlFrom={intl} model={model} onChange={onChange} />
        </IntlProvider>
      </AuthenticatedLocaleContextProvider>
    </div>
  );
};
